// Session 6 PR3 — interview prep chat hooks.
//
// State persistence + WS broadcast live on the InterviewPrepAgent DO; this
// module wraps the HTTP read-side (header / "is there an interview event?")
// and the POST chat / mark-debrief mutations. The SPA route consumes the
// live messages via `useAgent` (PartySocket bridge), not these hooks.

import { useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  InterviewPrepMessage,
  PrepSessionMeta,
} from "@resume-cloudflare/shared";
import { api, ApiError } from "./api";
import { useMutationToast } from "../hooks/useMutationToast";

export interface PrepStateResponse {
  session: PrepSessionMeta;
}

export function usePrepState(applicationId: string | null) {
  return useQuery<PrepStateResponse>({
    queryKey: ["interview-prep-state", applicationId],
    enabled: applicationId !== null,
    queryFn: () =>
      api<PrepStateResponse>(`/api/applications/${applicationId}/prep/state`),
    // Re-fetch on every page entry — the interview event might have just been
    // scheduled in the Sheet, so the cached state could be stale.
    staleTime: 0,
  });
}

export interface PrepChatResponse {
  assistant: InterviewPrepMessage;
  turns_count: number;
}

export function useSendPrepChat(applicationId: string) {
  const qc = useQueryClient();
  return useMutationToast<PrepChatResponse, PrepError, { message: string }>({
    mutationFn: async (input) => {
      try {
        return await api<PrepChatResponse>(
          `/api/applications/${applicationId}/prep/chat`,
          { method: "POST", body: JSON.stringify(input) },
        );
      } catch (err) {
        throw toApiError(err);
      }
    },
    onSuccess: () => {
      // Refresh turns_count + last_turn_at from D1.
      qc.invalidateQueries({
        queryKey: ["interview-prep-state", applicationId],
      });
    },
    success: false,
    errorPrefix: "Send failed",
  });
}

export function useMarkDebrief(applicationId: string) {
  const qc = useQueryClient();
  return useMutationToast<{ already?: boolean }, PrepError, void>({
    mutationFn: async () => {
      try {
        return await api<{ already?: boolean }>(
          `/api/applications/${applicationId}/prep/mark-debrief`,
          { method: "POST" },
        );
      } catch (err) {
        throw toApiError(err);
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ["interview-prep-state", applicationId],
      });
    },
    success: "Switched to debrief mode.",
    errorPrefix: "Mark complete failed",
  });
}

interface PrepError {
  status: number;
  error: string;
}

function toApiError(err: unknown): PrepError {
  if (err instanceof ApiError) {
    let payload: { error?: string } = {};
    try {
      payload = JSON.parse(err.message);
    } catch {
      // not JSON
    }
    return {
      status: err.status,
      error: payload.error ?? err.message ?? "request_failed",
    };
  }
  return { status: 0, error: "network_error" };
}
